import { useEffect, useMemo, useState } from 'react';

const STORAGE_PREFIX = 'qualyx.columns';

const loadStored = (storageKey) => {
  try {
    const raw = localStorage.getItem(`${STORAGE_PREFIX}.${storageKey}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed?.visible) ? parsed.visible : null;
  } catch {
    return null;
  }
};

/**
 * Controls which columns of a table are visible and persists the preference
 * in localStorage (per device). Locked columns (identification/actions) are
 * always kept visible.
 *
 * @param {string} storageKey - Unique key per screen/module.
 * @param {Array<{key: string, label: string, locked?: boolean, render: Function}>} columns - Column config.
 */
const useColumnVisibility = ({ storageKey, columns }) => {
  const lockedKeys = useMemo(
    () => columns.filter((col) => col.locked).map((col) => col.key),
    [columns],
  );
  const defaultVisible = useMemo(() => columns.map((col) => col.key), [columns]);

  const [visibleKeys, setVisibleKeys] = useState(() => {
    const stored = loadStored(storageKey);
    if (!stored) return defaultVisible;
    const known = columns.map((col) => col.key);
    const selected = new Set([...lockedKeys, ...stored.filter((key) => known.includes(key))]);
    return columns.map((col) => col.key).filter((key) => selected.has(key));
  });

  useEffect(() => {
    try {
      const optionalKeys = visibleKeys.filter((key) => !lockedKeys.includes(key));
      localStorage.setItem(
        `${STORAGE_PREFIX}.${storageKey}`,
        JSON.stringify({ v: 1, visible: optionalKeys }),
      );
    } catch {
      // Ignore quota / private-mode errors; preference simply won't persist.
    }
  }, [storageKey, visibleKeys, lockedKeys]);

  const visibleColumns = useMemo(
    () => columns.filter((col) => visibleKeys.includes(col.key)),
    [columns, visibleKeys],
  );

  const toggleColumn = (key) => {
    if (lockedKeys.includes(key)) return;
    setVisibleKeys((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key],
    );
  };

  const resetColumns = () => setVisibleKeys(defaultVisible);

  const hasCustom =
    JSON.stringify(visibleKeys.filter((k) => !lockedKeys.includes(k))) !==
    JSON.stringify(defaultVisible.filter((k) => !lockedKeys.includes(k)));

  return { visibleColumns, visibleKeys, toggleColumn, resetColumns, hasCustom };
};

export default useColumnVisibility;